<template>
	<card title="Charts" tag="section" v-if="hasData">
		<div class="is-flex is-justify-content-space-between">
			<div>
				<o-switch v-model="showCompare">Show Comparison</o-switch>
			</div>
			<div class="is-flex is-justify-content-space-around">
				<o-field label="Show" class="ml-5">
					<o-tooltip>
						<o-select v-model="metric">
							<option :value="s.metric" v-for="s in availableMetrics" :key="s.metric">
								{{ s.text }}
							</option>
						</o-select>
						<template #content>Options are loading please wait..</template>
					</o-tooltip>
				</o-field>
			</div>
		</div>
		<chart-legend show-current show-compare show-dashed></chart-legend>
		<line-chart :datasets="datasets" :labels="labels" :options="options"></line-chart>
	</card>
</template>
<script>
	import { ref, computed, inject, watch, onMounted, onBeforeUnmount } from "vue";
	import { lineChart, lineChartHelper, legend } from "@/components/charts";
	import { stringUtil } from "@/services/utils";

	export default {
		components: {
			lineChart,
			chartLegend: legend
		},
		props: {
			data: {
				type: Object,
				default: () => {
					return {};
				}
			},
			colors: {
				type: Array,
				default: () => {
					return ["#00aeef", "#d9017a"];
				}
			},
			sampleSize: {
				type: Number,
				default: null
			}
		},
		setup(props, context) {
			const http = inject("$http");
			const $financial = inject("$financial");
			const moment = inject("$moment");

			const showCompare = ref(true);
			const metric = ref("totalBillings");
			const year = ref(moment.utc().year());
			const years = ref([
				moment.utc().year(),
				moment.utc().subtract(1, "year").year(),
				moment.utc().subtract(2, "year").year()
			]);

			const labels = ref([]);
			const datasets = ref([]);
			const selectors = ref([
				{ metric: "totalBillings", text: "Total Billings" },
				{ metric: "teamingValue", text: "Teaming Value" },
				{ metric: "teamingRevenue", text: "Teaming Revenue" },
				{ metric: "executiveSearch", text: "Executive Search" },
				{ metric: "consulting", text: "Leadership Consulting" },
				{ metric: "im", text: "Interim Management" },
				{ metric: "strategy", text: "Strategy Billings" },
				{ metric: "filledJobValue", text: "Average Filled Job Value" },
				{ metric: "strategyTaken", text: "Strategy Taken" },
				{ metric: "taken", text: "Taken" },
				{ metric: "filled", text: "Filled" }
			]);

			const options = ref({
				responsive: true,
				plugins: {
					legend: {
						display: false
					},
					tooltip: {
						callbacks: {
							title: (tooltipItems) => {
								const tooltip = tooltipItems[0];
								const labelType = tooltip.dataset.label;
								if (labelType == "Previous Period") {
									const label = moment(tooltip.label, "MMM YYYY");
									label.add(-1, "year");
									return label.format("MMM YYYY");
								} else return tooltip.label;
							},
							label: (tooltipItem) => {
								return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
									tooltipItem.raw
								);
							}
						}
					}
				},
				scales: {
					y: {
						suggestedMin: 0
					},
					x: {
						ticks: {
							maxTicksLimit: props.sampleSize,
							sampleSize: props.sampleSize
						}
					}
				}
			});

			const UpdateChart = () => {
				const item = props.data[metric.value];
				if (item) {
					labels.value = item.labels;
					datasets.value = showCompare.value ? item.datasets : [item.datasets[0]];
				}
			};

			const availableMetrics = computed(() => {
				return selectors.value.filter((x) => x.hasData == true);
			});

			const hasData = computed(() => {
				return props.data && Object.keys(props.data).length > 0;
			});

			const Initialize = () => {
				if (props.data && Object.keys(props.data).length > 0) {
					const items = Object.entries(props.data);
					for (const [key, value] of items) {
						const reference = selectors.value.find((x) => x.metric == key);
						if (reference) {
							reference.labels = value.labels;
							reference.datasets = showCompare.value ? value.datasets : [value.datasets[0]];
							reference.hasData = true;
						}
					}

					//Load the data for totalBillings
					if (props.data["totalBillings"]) {
						labels.value = props.data["totalBillings"].labels;
						datasets.value = props.data["totalBillings"].datasets;
					}
				}
			};

			watch(
				() => metric.value,
				() => {
					UpdateChart();
				}
			);

			watch(
				() => showCompare.value,
				() => {
					UpdateChart();
				}
			);

			watch(
				() => props.data,
				() => {
					Initialize();
				}
			);

			onMounted(() => {
				Initialize();
			});

			return {
				metric,
				datasets,
				labels,
				options,
				showCompare,
				availableMetrics,
				hasData,
				UpdateChart
			};
		}
	};
</script>